import PropTypes from 'prop-types';
import IconTile from './IconTile';
import './primitives.css';

/**
 * SectionHeader
 *
 * One 40px band between blocks inside a page: title left, action right, on the
 * same baseline (`docs/design-language.md` §3 rule 4 — no accent bar, no 24px
 * of air above and below). Used standalone between sections, and by
 * `SectionCard` as its head, so a section reads the same whether or not it
 * happens to sit in a card.
 *
 *   <SectionHeader title={t('dashboard.today')} actions={<Button …/>} />
 *
 * `meta` is the small count or range that belongs to the title rather than to
 * the content — "12 ta", "6–12 sentabr". It shares the title's baseline.
 */
const SectionHeader = ({
  title,
  icon,
  tone = 'neutral',
  subject,
  meta,
  actions,
  level = 3,
  className = '',
  id,
}) => {
  const Heading = `h${level}`;

  return (
    <div className={['lms-section-header', className].filter(Boolean).join(' ')}>
      <div className="lms-section-header__title-block">
        {icon && <IconTile icon={icon} tone={tone} subject={subject} size="sm" />}
        {title && (
          <Heading className="lms-section-header__title" id={id}>
            {title}
          </Heading>
        )}
        {meta && <span className="lms-section-header__meta">{meta}</span>}
      </div>
      {actions && <div className="lms-section-header__actions">{actions}</div>}
    </div>
  );
};

SectionHeader.propTypes = {
  /** Optional only so a band can be actions-only; a titled band is the norm. */
  title: PropTypes.node,
  /** Rendered inside an IconTile — a bare glyph here would not be a band. */
  icon: PropTypes.node,
  tone: IconTile.propTypes.tone,
  subject: IconTile.propTypes.subject,
  meta: PropTypes.node,
  actions: PropTypes.node,
  /** Heading level, so a page's outline stays in order. */
  level: PropTypes.oneOf([2, 3, 4, 5]),
  className: PropTypes.string,
  id: PropTypes.string,
};

export default SectionHeader;
